import { useCallback, useMemo } from 'react'

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '.'
import {
  DeviceWidth,
  useIsDeviceWidth,
} from '../../../../../src/utils/deviceWidthHelpers'
const PAGE_BREAK = '...'

interface PaginationPagesProps {
  currentPage: number
  disabled?: boolean
  onPageClick?: (page: number) => void
  pages: number
}

export const PaginationPages = ({
  currentPage,
  disabled = false,
  onPageClick,
  pages,
}: PaginationPagesProps) => {
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const maxPageElements = useMemo(() => (isMobile ? 5 : 7), [isMobile])
  const paginationElementCount =
    pages > maxPageElements ? maxPageElements : pages

  const shouldRenderBreaks = useMemo(
    () => pages > maxPageElements,
    [pages, maxPageElements]
  )

  const inFirstSection = useMemo(
    () => currentPage <= (isMobile ? 2 : 4),
    [currentPage, isMobile]
  )

  const inLastSection = useMemo(
    () => currentPage >= pages - (isMobile ? 1 : 3),
    [currentPage, pages, isMobile]
  )

  const getButtonContents = useCallback<(index: number) => string>(
    (index: number) => {
      if (!shouldRenderBreaks) {
        return `${index + 1}`
      }

      const mobileContentsMap: { [key: number]: string | number } = {
        0: '1',
        1: inFirstSection ? '2' : PAGE_BREAK,
        2: inFirstSection ? '3' : inLastSection ? pages - 2 : currentPage,
        3: inLastSection ? pages - 1 : PAGE_BREAK,
        4: pages,
      }

      const contentsMap: { [key: number]: string | number } = {
        0: '1',
        1: inFirstSection ? '2' : PAGE_BREAK,
        2: inFirstSection ? '3' : inLastSection ? pages - 4 : currentPage - 1,
        3: inFirstSection ? '4' : inLastSection ? pages - 3 : currentPage,
        4: inFirstSection ? '5' : inLastSection ? pages - 2 : currentPage + 1,
        5: inLastSection ? pages - 1 : PAGE_BREAK,
        6: pages,
      }

      return isMobile ? `${mobileContentsMap[index]}` : `${contentsMap[index]}`
    },
    [
      currentPage,
      inFirstSection,
      inLastSection,
      pages,
      shouldRenderBreaks,
      isMobile,
    ]
  )

  const isCurrentPage = useCallback<(page: number, index: number) => boolean>(
    (page: number, index: number) => {
      if (!shouldRenderBreaks || inFirstSection) {
        return page - 1 === index
      }

      if (inLastSection) {
        return maxPageElements - (pages - page) - 1 === index
      }

      return index === (isMobile ? 2 : 3)
    },
    [
      inFirstSection,
      inLastSection,
      pages,
      shouldRenderBreaks,
      isMobile,
      maxPageElements,
    ]
  )

  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={() => {
              if (currentPage > 1) {
                onPageClick?.(currentPage - 1)
              }
            }}
          />
        </PaginationItem>
        {[...Array(paginationElementCount)].map((_key, index) => {
          const buttonContents = getButtonContents(index)
          const buttonPage = Number.parseInt(buttonContents)
          const isBreak = buttonContents === PAGE_BREAK

          return (
            <PaginationItem key={`PaginationItem_${index}`}>
              {isBreak ? (
                <PaginationEllipsis />
              ) : (
                <PaginationLink
                  isActive={isCurrentPage(currentPage, index)}
                  disabled={disabled}
                  onClick={() => onPageClick?.(buttonPage)}
                  aria-label={`Page ${buttonContents}`}
                >
                  {buttonContents}
                </PaginationLink>
              )}
            </PaginationItem>
          )
        })}
        <PaginationItem>
          <PaginationNext
            onClick={() => {
              if (currentPage < pages) {
                onPageClick?.(currentPage + 1)
              }
            }}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}
